import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardHeader, CardBody, PageError, PageLoading } from "../components/index.js";
import { Field } from "../components/AdminForm.jsx";
import { LineItemEditor } from "../components/LineItemEditor.jsx";
import { useApi, useApiAction } from "../hooks/useApi.js";
import { useAuth } from "../context/AuthContext.jsx";
import { loadSession } from "../lib/storage.js";
import { ProposalStatusChip, forLabel } from "./AgencyProposals.jsx";
import { priceLabel } from "./AdminCatalog.jsx";
import { LINE_ITEM_CATEGORY_LABELS } from "@prism/shared/constants";

/**
 * Proposal builder (/agency/proposals/:id) — Phase 4b-2.
 * Scope (shared LineItemEditor in API mode + catalog quick-add), Terms +
 * Internal notes with 500 ms debounced autosave, Investment summary sidebar.
 * Send / Resend / Revise / PDF (4b-3), Mark accepted (SUPER_ADMIN), Convert to contract.
 */
const fmtMoney = (v) => `$${Number(v ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const CATEGORY_ORDER = Object.keys(LINE_ITEM_CATEGORY_LABELS);

function useDebouncedSave(save, delay = 500) {
  const timer = useRef(null);
  const latest = useRef(null);
  const lastBody = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const queue = (patch) => {
    latest.current = { ...(latest.current || {}), ...patch };
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const body = latest.current; latest.current = null;
      lastBody.current = body;
      const ok = await save(body);
      if (!ok) latest.current = { ...body, ...(latest.current || {}) };
    }, delay);
  };
  const retry = () => (lastBody.current ? save(lastBody.current) : Promise.resolve());
  return { queue, retry };
}

export function ProposalBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApiAction();
  const { user } = useAuth();
  const superAdmin = user?.role === "SUPER_ADMIN";
  const { data, loading, error, reload } = useApi(`/api/proposals/${id}`);
  // Change #4: the editor's catalog picker. QuickAdd fetches its own copy inside
  // its own component; this one is the builder's, for the manual editor.
  const { data: catalogData } = useApi("/api/services?active=true");
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [actionErr, setActionErr] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [sending, setSending] = useState(false);

  const proposal = data?.proposal;
  const lines = data?.lineItems || [];
  const editable = proposal?.status === "draft";
  const party = data?.party;
  const sendable = ["draft", "sent", "expired"].includes(proposal?.status) && lines.length > 0;
  const fmtDate = (d) => new Date(d).toLocaleDateString();
  const fmtDateOnly = (d) => new Date(d).toLocaleDateString(undefined, { timeZone: "UTC" });

  // Open the tab synchronously (popup-blocker safe), then point it at a blob
  // fetched with the JWT — the API has no cookie session to lean on.
  async function openPdf() {
    setActionErr(null);
    const win = window.open("", "_blank");
    try {
      const res = await fetch(`/api/proposals/${id}/pdf`, { headers: { Authorization: `Bearer ${loadSession()?.token ?? ""}` } });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || `PDF failed (HTTP ${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (win) win.location = url; else window.open(url, "_blank");
    } catch (e) { win?.close(); setActionErr(e); }
  }

  async function put(body) {
    setSaveState("saving");
    try {
      await api(`/api/proposals/${id}`, { method: "PUT", body: JSON.stringify(body) });
      setSaveState("saved");
      await reload({ silent: true });
      return true;
    } catch (e) { setSaveState("error"); setActionErr(e); return false; }
  }
  const { queue: autosave, retry: retryAutosave } = useDebouncedSave(put);

  async function act(path, body, after) {
    setActionErr(null);
    try {
      const r = await api(`/api/proposals/${id}${path}`, { method: "POST", ...(body ? { body: JSON.stringify(body) } : {}) });
      if (after) await after(r); else await reload({ silent: true });
    } catch (e) { setActionErr(e); }
  }

  if (error) return <PageError error={error} onRetry={() => reload()} />;
  if (loading || !proposal) return <PageLoading />;

  return (
    <div className="max-w-[1200px] space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <button type="button" onClick={() => navigate("/agency/proposals")} className="text-[12px] text-text-muted hover:text-text-primary">← Proposals</button>
          <div className="mt-1 flex items-center gap-2">
            {editable ? (
              <input defaultValue={proposal.title} onChange={(e) => autosave({ title: e.target.value })}
                className="text-[18px] font-semibold text-text-primary bg-transparent border-b border-transparent focus:border-border-default focus:outline-none min-w-0 flex-1" />
            ) : (
              <h2 className="text-[18px] font-semibold text-text-primary">{proposal.title}</h2>
            )}
            <ProposalStatusChip status={proposal.status} />
          </div>
          <p className="text-[12px] text-text-muted mt-0.5">
            For {forLabel(proposal)}
            {proposal.status === "sent" && proposal.sentAt && ` · sent to ${proposal.sentToName || proposal.sentToEmail} on ${fmtDate(proposal.sentAt)}`}
            {proposal.status === "sent" && proposal.expiresAt && ` · link expires ${fmtDateOnly(proposal.expiresAt)}`}
            {proposal.status === "expired" && proposal.expiresAt && ` · expired ${fmtDateOnly(proposal.expiresAt)}`}
            {proposal.acceptedAt && ` · accepted ${fmtDate(proposal.acceptedAt)} by ${proposal.acceptedByName}${proposal.acceptedVia === "link" ? " (online)" : ""}`}
            {proposal.contractId && <> · <button type="button" onClick={() => navigate("/agency/contracts")} className="text-accent hover:underline">view contract</button></>}
            <span className="ml-2 text-text-muted">
              {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved" : saveState === "error" ? (
                <>Save failed · <button type="button" onClick={retryAutosave} className="text-accent hover:underline">Retry</button></>
              ) : ""}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {sendable && (
            <button type="button" onClick={() => setSending(true)} className="px-3 py-1.5 rounded-card bg-accent text-white text-[12px] font-medium">
              {proposal.status === "draft" ? "Send" : "Resend"}
            </button>
          )}
          {["sent", "expired"].includes(proposal.status) && (
            <button type="button" onClick={() => { if (confirm("Reopen for revision? The link the client received will stop working.")) act("/reopen"); }}
              className="px-3 py-1.5 rounded-card border border-border-default text-[12px] font-medium text-text-secondary hover:text-text-primary">Revise</button>
          )}
          {(proposal.status === "draft" || proposal.hasPdf) && (
            <button type="button" onClick={openPdf} className="px-3 py-1.5 rounded-card border border-border-default text-[12px] font-medium text-text-secondary hover:text-text-primary">PDF</button>
          )}
          {superAdmin && ["draft", "sent"].includes(proposal.status) && (
            <button type="button" onClick={() => setAccepting(true)} className="px-3 py-1.5 rounded-card border border-border-default text-[12px] font-medium text-text-secondary hover:text-text-primary">Mark accepted</button>
          )}
          {proposal.status === "accepted" && !proposal.contractId && (
            <button type="button" onClick={() => act("/convert", null, () => navigate("/agency/contracts"))} className="px-3 py-1.5 rounded-card bg-accent text-white text-[12px] font-medium">Convert to contract</button>
          )}
          {["draft", "sent", "expired"].includes(proposal.status) && (
            <button type="button" onClick={() => { if (confirm("Mark this proposal rejected? Any accept link stops working. This cannot be undone.")) put({ status: "rejected" }); }} className="px-3 py-1.5 rounded-card border border-border-default text-[12px] text-text-muted hover:text-negative">Mark rejected</button>
          )}
          {superAdmin && editable && (
            <button type="button" onClick={async () => { if (!confirm("Delete this draft proposal?")) return; await api(`/api/proposals/${id}`, { method: "DELETE" }); navigate("/agency/proposals"); }}
              className="px-3 py-1.5 rounded-card border border-border-default text-[12px] text-text-muted hover:text-negative">Delete</button>
          )}
        </div>
      </div>
      {actionErr && <p className="text-[12px] text-negative">{actionErr.message}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4 items-start">
        <div className="space-y-4">
          <Card>
            <CardHeader title="Scope" subtitle={editable ? "Line items · edit or quick-add from the catalog" : "Line items"} />
            <CardBody>
              <LineItemEditor
                items={lines}
                editable={editable}
                services={catalogData?.services ?? []}
                onCreate={async (values) => { await api(`/api/proposals/${id}/line-items`, { method: "POST", body: JSON.stringify(values) }); await reload({ silent: true }); }}
                onUpdate={async (lid, values) => { await api(`/api/proposals/${id}/line-items/${lid}`, { method: "PUT", body: JSON.stringify(values) }); await reload({ silent: true }); }}
                onRemove={async (lid) => { await api(`/api/proposals/${id}/line-items/${lid}`, { method: "DELETE" }); await reload({ silent: true }); }}
              />
            </CardBody>
          </Card>

          {editable && <QuickAdd proposalId={id} superAdmin={superAdmin} onAdded={() => reload({ silent: true })} />}

          <Card>
            <CardHeader title="Terms" subtitle="Client-facing — appears on the proposal document" />
            <CardBody>
              <textarea defaultValue={proposal.notes || ""} disabled={!editable} onChange={(e) => autosave({ notes: e.target.value })}
                rows={5} className="w-full rounded border border-border-default px-2.5 py-2 text-[13px] bg-surface disabled:opacity-70" placeholder="Payment terms, timeline, assumptions…" />
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Internal notes" subtitle="Staff only" />
            <CardBody>
              <textarea defaultValue={proposal.internalNotes || ""} onChange={(e) => autosave({ internalNotes: e.target.value })}
                rows={3} className="w-full rounded border border-border-default px-2.5 py-2 text-[13px] bg-surface" />
              <p className="mt-1 text-[11px] text-text-muted">Editable at any stage · staff only</p>
            </CardBody>
          </Card>
        </div>

        <Card className="lg:sticky lg:top-4">
          <CardHeader title="Investment summary" />
          <CardBody className="space-y-2 text-[13px]">
            <Row label="Setup / one-time" value={fmtMoney(proposal.totalSetupFees)} />
            <Row label="Monthly recurring" value={`${fmtMoney(proposal.totalMonthlyRecurring)}/mo`} />
            <Row label="Annual recurring" value={`${fmtMoney(proposal.totalAnnualRecurring)}/yr`} />
            <div className="border-t border-border-light pt-2 text-[12px] text-text-muted">{lines.length} line item{lines.length === 1 ? "" : "s"} · quarterly lines count at ⅓ toward monthly</div>
          </CardBody>
        </Card>
      </div>

      {accepting && (
        <MarkAcceptedModal onClose={() => setAccepting(false)} onConfirm={async (body) => { setAccepting(false); await act("/mark-accepted", body); }} />
      )}
      {sending && (
        <SendModal
          proposal={proposal}
          party={party}
          onClose={() => setSending(false)}
          onSend={async (body) => { setSending(false); await act("/send", body); }}
        />
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-text-secondary">{label}</span>
      <span className="font-medium text-text-primary" style={{ fontFeatureSettings: "'tnum'" }}>{value}</span>
    </div>
  );
}

/** Catalog quick-add. `endpoint` overrides the proposal URL (the contract drawer passes its own). */
export function QuickAdd({ proposalId, superAdmin, onAdded, endpoint }) {
  const api = useApiAction();
  const url = endpoint ?? `/api/proposals/${proposalId}/quick-add`;
  const { data } = useApi("/api/services?active=true");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [projectTotal, setProjectTotal] = useState("");
  const [msg, setMsg] = useState(null);
  const services = useMemo(() => (data?.services || []).filter((s) => !q || `${s.name} ${s.category}`.toLowerCase().includes(q.toLowerCase())), [data, q]);
  const grouped = useMemo(() => {
    const m = new Map();
    for (const s of services) (m.get(s.category) ?? m.set(s.category, []).get(s.category)).push(s);
    const rank = (c) => { const i = CATEGORY_ORDER.indexOf(c); return i === -1 ? CATEGORY_ORDER.length : i; };
    return [...m.entries()].sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]));
  }, [services]);

  async function add(s) {
    setMsg(null);
    // A milestone-priced service needs the project total up front — otherwise
    // both lines land as "50% of $0". Ask for it here if the field is empty.
    let total = projectTotal;
    if (s.pricingModel === "project" && !(Number(total) > 0)) {
      const entered = prompt(`Project total ($) for ${s.name}:`);
      if (entered === null) return;
      if (!(Number(entered) > 0)) { setMsg("Enter a project total greater than 0."); return; }
      total = entered;
      setProjectTotal(entered);
    }
    setBusyId(s.id);
    try {
      const body = { serviceId: s.id, ...(s.pricingModel === "project" ? { projectTotal: Number(total) } : {}) };
      await api(url, { method: "POST", body: JSON.stringify(body) });
      await onAdded();
    } catch (e) { setMsg(e.message); } finally { setBusyId(null); }
  }

  return (
    <Card>
      <CardHeader title="Quick-add from catalog" subtitle="Click to add · prices snapshot onto the line" />
      <CardBody className="space-y-3">
        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Search" value={q} onChange={setQ} placeholder="elementor, social, hosting…" />
          <Field label="Project total for website builds ($)" type="number" value={projectTotal} onChange={setProjectTotal} />
        </div>
        {msg && <p className="text-[12px] text-text-secondary">{msg}</p>}
        {grouped.length === 0 && <p className="text-[12px] text-text-muted">No catalog items yet — add them under Admin → Catalog.</p>}
        {grouped.map(([cat, items]) => (
          <div key={cat}>
            <div className="text-[11px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.05em" }}>{cat}</div>
            <div className="flex flex-wrap gap-1.5">
              {items.map((s) => {
                const noPrice = s.basePrice == null && !["percentage", "project"].includes(s.pricingModel);
                return (
                  <button key={s.id} type="button" disabled={busyId === s.id || noPrice} onClick={() => add(s)} title={noPrice ? "Set clientPrice in the catalog sheet first" : s.description || ""}
                    className="h-7 px-2.5 rounded-card border border-border-default text-[12px] text-text-secondary hover:text-text-primary hover:border-accent disabled:opacity-50">
                    {s.name} <span className="text-text-muted">· {priceLabel(s)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}

function MarkAcceptedModal({ onClose, onConfirm }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-[440px] max-w-full bg-surface rounded-card shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[15px] font-semibold text-text-primary mb-1">Mark accepted</h2>
        <p className="text-[12px] text-text-muted mb-3">Records a verbal or emailed acceptance. A prospect moves to Won. This cannot be undone.</p>
        <div className="space-y-2.5">
          <Field label="Accepted by (name)" required value={name} onChange={setName} />
          <Field label="Email (optional)" value={email} onChange={setEmail} />
          <Field label="Note (optional)" value={note} onChange={setNote} placeholder="e.g. agreed on the 9/8 call" />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[13px] text-text-secondary">Cancel</button>
          <button type="button" disabled={!name.trim()} onClick={() => onConfirm({ acceptedByName: name.trim(), ...(email.trim() ? { acceptedByEmail: email.trim() } : {}), ...(note.trim() ? { note: note.trim() } : {}) })}
            className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium disabled:opacity-50">Confirm</button>
        </div>
      </div>
    </div>
  );
}

function SendModal({ proposal, party, onClose, onSend }) {
  const resend = proposal.status !== "draft";
  const [toName, setToName] = useState(proposal.sentToName || party?.contactName || "");
  const [toEmail, setToEmail] = useState(proposal.sentToEmail || party?.contactEmail || "");
  const [message, setMessage] = useState("");
  const ready = toName.trim().length > 0 && /\S+@\S+\.\S+/.test(toEmail);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-[480px] max-w-full bg-surface rounded-card shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[15px] font-semibold text-text-primary mb-1">{resend ? "Resend proposal" : "Send proposal"}</h2>
        <p className="text-[12px] text-text-muted mb-3">
          Emails the PDF with a one-click accept link, valid 30 days{proposal.expiresAt && proposal.status === "sent" ? ` (current expiry ${new Date(proposal.expiresAt).toLocaleDateString(undefined, { timeZone: "UTC" })})` : ""}.
          {resend ? " Resending replaces the previous link." : " The proposal locks for editing once sent."}
        </p>
        <div className="space-y-2.5">
          <Field label="Recipient name" required value={toName} onChange={setToName} />
          <Field label="Recipient email" type="email" required value={toEmail} onChange={setToEmail} />
          <div>
            <label className="block text-[10px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.5px" }}>Personal message (optional)</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} maxLength={1000}
              className="w-full rounded border border-border-default px-2.5 py-2 text-[13px] bg-surface" placeholder="Great to meet you last week — here's what we discussed." />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[13px] text-text-secondary">Cancel</button>
          <button type="button" disabled={!ready} onClick={() => onSend({ toName: toName.trim(), toEmail: toEmail.trim(), ...(message.trim() ? { message: message.trim() } : {}) })}
            className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium disabled:opacity-50">{resend ? "Resend" : "Send"}</button>
        </div>
      </div>
    </div>
  );
}
